export const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  'http://localhost:8000'

const ACCESS_KEY = 'xo_admin_access'
const REFRESH_KEY = 'xo_admin_refresh'

export const tokenStore = {
  getAccess(): string | null {
    return localStorage.getItem(ACCESS_KEY)
  },
  getRefresh(): string | null {
    return localStorage.getItem(REFRESH_KEY)
  },
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access)
    localStorage.setItem(REFRESH_KEY, refresh)
  },
  setAccess(access: string): void {
    localStorage.setItem(ACCESS_KEY, access)
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }
}

export class ApiError extends Error {
  status: number
  detail?: string
  constructor(status: number, message: string, detail?: string) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStore.getRefresh()
  if (!refresh) return null

  const res = await fetch(`${API_BASE_URL}/api/admin/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh })
  })

  if (!res.ok) {
    tokenStore.clear()
    return null
  }

  const data = (await res.json()) as { access: string; refresh?: string }
  tokenStore.setAccess(data.access)
  if (data.refresh) tokenStore.set(data.access, data.refresh)
  return data.access
}

type RequestInitJSON = Omit<RequestInit, 'body'> & { body?: unknown }

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInitJSON = {}
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`
  const buildHeaders = (token: string | null): HeadersInit => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined)
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return headers
  }

  const send = async (token: string | null) =>
    fetch(url, {
      ...init,
      headers: buildHeaders(token),
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined
    })

  let response = await send(tokenStore.getAccess())

  if (response.status === 401 && tokenStore.getRefresh()) {
    const newAccess = await refreshAccessToken()
    if (newAccess) {
      response = await send(newAccess)
    }
  }

  if (!response.ok) {
    let detail: string | undefined
    try {
      const data = (await response.json()) as { detail?: string }
      detail = data?.detail
    } catch {
      // ignore
    }
    throw new ApiError(
      response.status,
      detail ?? `Request failed (${response.status})`,
      detail
    )
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export interface AdminUser {
  id: number
  username: string
  email: string
  is_superuser: boolean
  is_staff: boolean
}

export interface LoginResponse {
  access: string
  refresh: string
  user: AdminUser
}

export async function login(
  identifier: string,
  password: string
): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE_URL}/api/admin/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username: identifier, password })
  })

  if (!res.ok) {
    let detail: string | undefined
    try {
      const data = (await res.json()) as { detail?: string }
      detail = data?.detail
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail ?? 'Login failed.', detail)
  }

  const data = (await res.json()) as LoginResponse
  tokenStore.set(data.access, data.refresh)
  return data
}

export async function fetchMe(): Promise<AdminUser> {
  return apiFetch<AdminUser>('/api/admin/me/')
}

export function logout(): void {
  tokenStore.clear()
}
